/**
 * DashboardSidebar — persistent iPadOS-style navigation rail.
 *
 * Rendered once as a sibling of <Routes> (see App.jsx), so it does NOT
 * remount on navigation — the sidebar stays put while the content pane swaps.
 *
 * Two nav groups:
 *  - Dashboard: the Command Center's analytical views, driven by /command-center?tab=
 *  - Workspace: top-level destinations (own routes)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  AlertCircle,
  Shield,
  TrendingUp,
  ClipboardList,
  Users,
  HardHat,
  Building2,
  Calendar,
  FileText,
  BarChart3,
  GraduationCap,
  BookOpenCheck,
  Sun,
  Moon,
  PanelLeft,
} from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { useAuth } from '../../auth/AuthProviderUnified';
import './DashboardSidebar.css';

// Mirrors routeConfig RequireAuth lists. Filter keys off user?.role (live
// sidebar mechanism) — do not switch to currentMembership.role here (ISS-570).
const GC_ADMIN_ROLES = ['gcAdmin', 'scAdmin', 'admin'];
const GC_DASHBOARD_ROLES = ['gcAdmin', 'scAdmin', 'admin', 'contractor', 'foreman', 'safety_supervisor', 'subcontractor'];

const DASHBOARD_VIEWS = [
  { tab: 'actions', label: 'Overview', icon: LayoutGrid },
  { tab: 'risk', label: 'Risk', icon: AlertCircle, roles: GC_DASHBOARD_ROLES },
  { tab: 'compliance', label: 'Compliance', icon: Shield, roles: GC_DASHBOARD_ROLES },
  { tab: 'trends', label: 'Trends', icon: TrendingUp, roles: GC_DASHBOARD_ROLES },
  { tab: 'audit', label: 'Audit', icon: ClipboardList, adminOnly: true },
];

const WORKSPACE = [
  { path: '/workers', label: 'Workers', icon: Users, roles: GC_ADMIN_ROLES },
  { path: '/foreman', label: 'Foreman', icon: HardHat, roles: ['gcAdmin', 'scAdmin', 'admin', 'foreman'] },
  { path: '/projects', label: 'Sites', icon: Building2 },
  { path: '/schedule', label: 'Schedule', icon: Calendar },
  { path: '/reports', label: 'Reports', icon: FileText, roles: ['gcAdmin', 'scAdmin', 'admin', 'worker'] },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/assets', label: 'Assets', icon: ClipboardList, roles: GC_ADMIN_ROLES },
  { path: '/training', label: 'Training', icon: GraduationCap, roles: GC_ADMIN_ROLES },
  // Learning-admin console: route requires admin|gcAdmin|scAdmin (server requireAdmin mirror)
  { path: '/learning-admin', label: 'Learning Admin', icon: BookOpenCheck, roles: ['admin', 'gcAdmin', 'scAdmin'] },
  { path: '/incidents', label: 'Incidents', icon: AlertCircle, roles: GC_ADMIN_ROLES },
];

export default function DashboardSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();

  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('vh_sidebar_collapsed') === '1'
  );
  useEffect(() => {
    localStorage.setItem('vh_sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  const canViewAudit = ['admin', 'gcAdmin'].includes(user?.role);
  const onCommandCenter = location.pathname === '/command-center';
  const currentTab = new URLSearchParams(location.search).get('tab');

  const isViewActive = (tab) => {
    if (!onCommandCenter) return false;
    if (tab === 'actions') return !currentTab || currentTab === 'actions';
    return currentTab === tab;
  };

  const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <aside className={`app-sidebar ${collapsed ? 'is-collapsed' : ''}`}>
      <div className="app-sidebar-brand">
        <span
          className="app-sidebar-logo"
          role="img"
          aria-label="VH Intelligence"
          onClick={() => navigate('/command-center')}
        />
      </div>

      <nav className="app-sidebar-nav" aria-label="Primary navigation">
        <div className="app-nav-section-label">Dashboard</div>
        {DASHBOARD_VIEWS.filter((v) => {
          if (v.adminOnly && !canViewAudit) return false;
          if (v.roles && !v.roles.includes(user?.role)) return false;
          return true;
        }).map((v) => {
          const Icon = v.icon;
          return (
            <button
              key={v.tab}
              type="button"
              className={`app-nav-item ${isViewActive(v.tab) ? 'active' : ''}`}
              onClick={() =>
                navigate(v.tab === 'actions' ? '/command-center' : `/command-center?tab=${v.tab}`)
              }
            >
              <Icon size={19} className="app-nav-icon" />
              <span className="app-nav-label">{v.label}</span>
            </button>
          );
        })}

        <div className="app-nav-section-label">Workspace</div>
        {WORKSPACE.filter((item) => !item.roles || item.roles.includes(user?.role)).map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              type="button"
              className={`app-nav-item ${isPathActive(item.path) ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <Icon size={19} className="app-nav-icon" />
              <span className="app-nav-label">{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="app-sidebar-footer">
        <button
          type="button"
          className="app-nav-item"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <PanelLeft size={19} className="app-nav-icon" />
          <span className="app-nav-label">Collapse</span>
        </button>
        <button
          type="button"
          className="app-nav-item"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        >
          {theme === 'dark' ? (
            <Sun size={19} className="app-nav-icon" />
          ) : (
            <Moon size={19} className="app-nav-icon" />
          )}
          <span className="app-nav-label">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
      </div>
    </aside>
  );
}
